import { Form } from "antd";
import { useEffect, useState } from "react";
import { retry } from "../../utils/http";
import { nameOf } from "../../utils/typescript";
import { QueryOperation } from "../../code/search-query/search-query";
import { ReportForm } from "./ReportsFormDialog.default";
import {
  addWellbeingReport,
  deleteWellbeingReport,
  getCompanyEmployees,
  getReportTargetCompanies,
  getReportTargetDepartments,
  getReportTargetAssets,
} from "../../repository/data.repository";
import { EmployeeUser } from "../../repository/data.default";
import DynamicForm from "../../controls/DynamicForm";
import InModal from "../../controls/InModal";
import { modalTitle } from "../../controls/InModal.default";
import ModalFooter from "../../controls/ModalFooter";
import ModalDeleteConfirmation from "../../controls/ModalDeleteConfirmation";
import ModalErrorAlert from "../../controls/ModalErrorAlert";

export interface ReportsFormDialogProps {
  readonly open?: boolean;
  // Create a draft report, or confirm a delete. Reviewers/files + lifecycle live in the Manage dialog.
  readonly mode: "add" | "delete";
  readonly initialValues?: ReportForm;
  readonly onFinish?: () => void;
  readonly onCancel?: () => void;
}

function ReportsFormDialog(props: ReportsFormDialogProps) {
  const [form] = Form.useForm();
  const values = Form.useWatch<ReportForm>([], form);

  const [valid, setValid] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (props.open) {
      form.setFieldsValue(props.initialValues ?? {});
    } else {
      reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.open]);

  useEffect(() => {
    form
      .validateFields({ validateOnly: true })
      .then((value) => setValid(!!Object.values(value).length))
      .catch(() => setValid(false));
  }, [form, values, props.open]);

  function onSubmit(): void {
    setLoading(true);
    setError(false);
    if (props.mode === "add") {
      const { company, user, department, asset, summary } = values;
      addWellbeingReport({
        summary,
        companyUid: company.value!,
        reportingForUserUid: user?.value,
        departmentUid: department?.value,
        assetUid: asset?.value,
      })
        .then(() => {
          setLoading(false);
          props.onFinish?.();
        })
        .catch(() => {
          setLoading(false);
          setError(true);
        });
    } else if (props.mode === "delete") {
      deleteWellbeingReport(props.initialValues?.uid!)
        .then(() => {
          setLoading(false);
          props.onFinish?.();
        })
        .catch(() => {
          setLoading(false);
          setError(true);
        });
    }
  }

  function onCancel(): void {
    props.onCancel?.();
  }

  // Methods

  function reset(): void {
    setLoading(false);
    setValid(false);
    setError(false);
    form.resetFields();
  }

  const matches = (text: string, search: string) =>
    text.toLowerCase().includes((search ?? "").toLowerCase());

  // The author picks the company (or sub-company) they were tasked on; the picker is reachability-scoped
  // server-side, so affiliates only see their assigned companies and admins see all.
  const companiesRequest = (search: string) =>
    getReportTargetCompanies().then((items) =>
      items
        .filter((c) => matches(c.name, search))
        .map((c) => ({ display: c.name, value: c.uid })),
    );

  // Optional narrowings — only meaningful once a company is chosen (cleared via dependsOn when it changes).
  const departmentsRequest = (search: string) => {
    const companyUid = values?.company?.value;
    if (!companyUid) return Promise.resolve([]);
    return getReportTargetDepartments(companyUid).then((items) =>
      items
        .filter((d) => matches(d.name, search))
        .map((d) => ({ display: d.name, value: d.uid })),
    );
  };

  const assetsRequest = (search: string) => {
    const companyUid = values?.company?.value;
    if (!companyUid) return Promise.resolve([]);
    return getReportTargetAssets(companyUid).then((items) =>
      items
        .filter((a) => matches(`${a.name} ${a.code ?? ""}`, search))
        .map((a) => ({
          display: a.code ? `${a.name} (${a.code})` : a.name,
          value: a.uid,
        })),
    );
  };

  const companyEmployeesRequest = (search: string) => {
    const conditions: any[] = [
      { field: nameOf<EmployeeUser>("firstName"), operation: QueryOperation.StartsWith, values: [search] },
    ];
    if (values?.company?.value)
      conditions.push({ field: nameOf<EmployeeUser>("companyUid"), operation: QueryOperation.Equal, values: [values.company.value] });

    return retry(() =>
      getCompanyEmployees({ searchQuery: { conditions }, pageNumber: 0, pageSize: 100 }),
    ).then(({ data }) =>
      data.map((dataItem) => ({
        display: dataItem.userName,
        subDisplay: dataItem.companyName,
        value: dataItem.uid,
      })),
    );
  };

  return (
    <InModal
      open={props.open}
      size={props.mode === "delete" ? "small" : "medium"}
      title={modalTitle(props.mode, "Report", props.initialValues?.user?.display)}
      onCancel={onCancel}
      actions={
        <ModalFooter
          mode={props.mode}
          loading={loading}
          valid={valid}
          onCancel={onCancel}
          onSubmit={onSubmit}
        />
      }
    >
      {props.mode === "add" && (
        <DynamicForm
          className="w-auto"
          form={form}
          schema={{
            groups: [
              {
                controls: [
                  {
                    type: "combobox",
                    name: "company",
                    label: "Company",
                    request: companiesRequest,
                    rules: [{ required: true, message: "Company is required" }],
                  },
                  {
                    type: "combobox",
                    name: "user",
                    label: "Subject",
                    request: companyEmployeesRequest,
                    dependsOn: ["company"],
                  },
                  {
                    type: "combobox",
                    name: "department",
                    label: "Department",
                    request: departmentsRequest,
                    dependsOn: ["company"],
                  },
                  {
                    type: "combobox",
                    name: "asset",
                    label: "Asset",
                    request: assetsRequest,
                    dependsOn: ["company"],
                  },
                  {
                    type: "areabox",
                    name: "summary",
                    label: "Summary",
                    rows: 5,
                    rules: [{ required: true, message: "Summary is required" }],
                  },
                ],
              },
            ],
          }}
        />
      )}
      {props.mode === "delete" && (
        <ModalDeleteConfirmation
          entityLabel="report"
          entityName={props.initialValues?.user?.display}
        />
      )}
      <ModalErrorAlert
        message={error ? "Something went wrong, try again" : undefined}
      />
    </InModal>
  );
}

export default ReportsFormDialog;
